<template>
  <div name="reports-events">
    <div v-if="hasAccess">
    <header class="px-6 mx-4">
      <div class="flex justify-between">
        <div>
          <h2 class="text-xl font-semibold w-64 inline-block">Events</h2>
          <div class="text-gray-500 pb-4">
            Events charts and raw data extracts.
          </div>
        </div>
        <div>
          <button
            type="button"
            class="filter-bar-button focus:outline-none hover:opacity-75"
            v-bind:class="showingCharts ? 'filter-item-button-on' : 'bg-white'"
            @click="switchTypeView()"
          >
            <i class="fas fa-chart-pie text-lg pt-1"></i>
          </button>
          <button
            type="button"
            class="filter-bar-button focus:outline-none hover:opacity-75"
            v-bind:class="!showingCharts ? 'filter-item-button-on' : 'bg-white'"
            @click="switchTypeView()"
          >
            <i class="fas fa-table text-lg pt-1"></i>
          </button>
        </div>
      </div>
    </header>

    <transition name="fade" v-if="!finishedLoading">
      <div class="loading">
        <span class="fa fa-spinner fa-spin"></span> Loading Reports
      </div>
    </transition>

    <div class="h-screen max-h-screen overflow-y-hidden">

    <div v-if="errored" class="ml-10 mt-4 font-bold text-red-400">
      <p>
        We are not able to retrieve this information at the moment, please try
        back later
      </p>
    </div>

      <div class="h-screen max-h-screen overflow-y-auto" id="content-area" v-else>
      <div id="printable" class="" v-if="finishedLoading">
        <div v-if="hasData">
          <eventChart
            v-show="chartsInView"
            :events="eventsFiltered"
            :pupilCount="uniquePupils"
          ></eventChart>
          <eventExtract
            v-show="dataInView"
            :events="eventsFiltered"
            :years="years"
            :types="types"
          ></eventExtract>
        </div>
        <p v-else class="ml-10">
          There is no data available for the given filter selection.
        </p>
      </div>
    </div>

 

    <div style="visibility: hidden !important;">
      <vue-html2pdf
        :show-layout="false"
        :float-layout="false"
        :enable-download="true"
        :preview-modal="false"
        :paginate-elements-by-height="1100"
        :filename="printingFilename"
        :pdf-quality="2"
        :manual-pagination="true"
        pdf-format="a4"
        :margin="10"
        pdf-orientation="portrait"
        pdf-content-width="800px"
        ref="html2PdfEvent"
      >
        <section slot="pdf-content">
          <div
            class="w-full  mb-4"
            style="
              background-image: url('static/images/report-cover-assessment.png');
              background-position: top center;
              background-repeat: no-repeat;
              background-size: 80% 96%;
              height: 80em;
            "
          >
            <div
              class="flex flex-col pl-10p sm:pl-0p items-center justify-center"
            >
              <div class="w-3/5 rounded mx-4 div-extra flex flex-col text-center justify-center p-6 bg-white mt-16">
                <h4 class="pb-2 text-lg font-bold">
                 {{ printTitle }}
                </h4>
                <h2 class="font-sans font-extrabold text-4xl text-action-blue">{{ printYear }}</h2>
                <h3 class="font-sans font-black text-gray-800 text-3xl uppercase leading-9">Events Report</h3>
               
              </div>
            </div>

            <div class="right bottom mr-20 block" style="margin-top: 45em;">
            <img
            src="/static/images/logo-blank.png"
            height="90px"
            width="90px"

          />
        </div>
          </div>
          <div class="html2pdf__page-break">&nbsp;</div>
          <eventChart
            :events="eventsFiltered"
            :pupilCount="uniquePupils"
          ></eventChart>
          <div class="html2pdf__page-break">&nbsp;</div>
          <eventExtract
            :events="eventsFiltered"
            :years="years"
            :types="types"
          ></eventExtract>

          <div class="mb-4 ml-6 opacity-75">
           <p>{{ printInfo }}</p>
           <p>{{ filterInfo }}</p>
        </div>

          <div
            class="bg-pepa-red mb-8 p-2 px-6 text-right text-white text-xs pb-4 flex justify-between"
          >
            <div>Events - {{ printTitle }} {{  printYear }}</div>
            <div>&copy; PE Passport Ltd</div>
          </div>
        </section>
      </vue-html2pdf>
    </div>
    </div>
    </div>
     <div class="ml-4" v-else>
        <UpgradeContent :area="'Reporting'"></UpgradeContent>
      </div>

  </div>
</template>

<script>
import { mapGetters } from "vuex";
import VueHtml2pdf from "vue-html2pdf";
import moment from 'moment'

import { ReportingService } from "@/services/reporting.service";

import { pupilMixin } from "@/mixins/pupilMixin.js";

import { calendarYearList, filterTypeList } from "@/assets/js/utils";

import("@/components/charts/TheAssessmentChart.vue");
const eventChart = () => import("@/components/charts/TheEventChart.vue");
const eventExtract = () => import("@/components/reports/TheEventData.vue");
const UpgradeContent = () => import("@/components/upgrade/UpgradeContent.vue");

export default {
  name: "reports-events",
  props: ["filterValues", "hasAccess"],
  mixins: [pupilMixin],
  computed: {
    ...mapGetters({
      currentUser: "currentUser",
      currentAccess: "currentAccess",
    }),
    printYear() {
      let calendarYear = calendarYearList().find((y) => y.current === true);
      return calendarYear.short
    },
    printTitle() {
      return this.currentUser.schoolName;
    },
    printInfo() {
      return "The Events report was exported on " + moment().format("DD MM YYYY hh:mm:ss") + " by " + this.currentUser.teacherName
    },
    filterInfo() {
      let intro = "The report is currently filtering on. "
      return intro + this.filterValues.calendar.name
    },
    printingFilename() {
      let calendarYear = calendarYearList().find((y) => y.current === true);
      let name =
        this.currentUser.schoolName.replace(" ", "_") +
        "_ec_activities_" +
        calendarYear.short;
      return name.toLowerCase();
    },
    hasData() {
      return this.events.length > 0;
    },
    chartsInView() {
      return this.showingCharts === true;
    },
    dataInView() {
      return this.showingCharts === false;
    },
    uniquePupils() {
        return this.pupilCount
    },
    eventsFiltered() {
      return this.events;
    },
  },
  data() {
    return {
      types: filterTypeList(),
      loading: true,
      finishedLoading: false,
      errored: false,
      showingCharts: true,
      events: [],
      pupilCount: 0
    };
  },
  created() {},
  mounted() {
    this.refresh();
  },
  watch: {
    loading(newValue) {
      setTimeout(() => {
        this.finishedLoading = !newValue;
      }, 2000);
    },
    $props: {
      handler: function (val, oldVal) {
        this.refresh();
      },
      deep: true,
    },
  },
  methods: {
    refresh() {
      this.fetchReportingData();
    },
    async fetchReportingData() {
      try {
        this.fetchEvents(this.filterValues);
      } catch (err) {
        console.log("Activities " + err);
        this.reportErrored = true;
      } finally {
        this.reportLoading = false;
      }
    },
    fetchEvents(values) {
      ReportingService.getEvents(values).then(
        (response) => (this.parseEventData(response.data))
      );
    },
    parseEventData(data) {
      this.events = data.data
      this.pupilCount = data.pupil_count
    },
    switchTypeView() {
      this.showingCharts = !this.showingCharts;
      this.refresh();
    },
    generatePrint(path) {
      this.$refs.html2PdfEvent.generatePdf();
    },
  },
  components: {
    eventChart,
    eventExtract,
    VueHtml2pdf,
    UpgradeContent
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>